import React, { useEffect, useRef, useState } from "react";
import { resolveImg, proxyImg } from "@/lib/api";
import { Play, Pause, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

function parseMs(tc, fallback) {
  const cap = (d) => Math.min(Math.max(d, 1200), 6000);
  if (typeof tc === "string") {
    const m = tc.match(/(\d+)\s*-\s*(\d+)/);
    if (m) {
      const d = (parseInt(m[2], 10) - parseInt(m[1], 10)) * 1000;
      if (d > 0) return cap(d);
    }
  }
  return cap(fallback);
}

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCover(ctx, img, cw, ch, scale, panX, panY) {
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const s = Math.max(cw / iw, ch / ih) * scale;
  const dw = iw * s, dh = ih * s;
  ctx.drawImage(img, (cw - dw) / 2 + panX, (ch - dh) / 2 + panY, dw, dh);
}

function wrapText(ctx, text, maxWidth) {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, 4);
}

export default function VideoPreview({ photos = [], storyboard = [], caption = "", style = null, badge = "" }) {
  const accent = style?.accent || "#D4AF37";
  const scenes = storyboard && storyboard.length ? storyboard : [{ on_screen_text: caption }];
  const n = scenes.length;
  const rawDur = scenes.map((s) => parseMs(s.timecode, Math.round(15000 / n)));
  const rawSum = rawDur.reduce((a, b) => a + b, 0);
  const fit = rawSum > 15000 ? 15000 / rawSum : 1;
  const durations = rawDur.map((d) => Math.round(d * fit));

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [recording, setRecording] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return;
    timer.current = setTimeout(() => {
      if (idx >= n - 1) { setPlaying(false); setIdx(0); }
      else setIdx((i) => i + 1);
    }, durations[idx] || 3000);
    return () => clearTimeout(timer.current);
  }, [playing, idx, n]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (playing) { setPlaying(false); return; }
    setIdx(0);
    setPlaying(true);
  };

  const downloadVideo = async () => {
    if (!photos.length) { toast.error("Add photos to this boat first"); return; }
    if (typeof MediaRecorder === "undefined") { toast.error("Your browser can't record video. Try Chrome."); return; }
    setPlaying(false);
    setRecording(true);
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const W = 720, H = 1280;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      const imgs = await Promise.all(scenes.map((_, i) => loadImage(proxyImg(photos[i % photos.length]))));

      const stream = canvas.captureStream(30);
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp8") ? "video/webm;codecs=vp8" : "video/webm");
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      const stopped = new Promise((res) => { rec.onstop = res; });
      rec.start();

      const total = durations.reduce((a, b) => a + b, 0);
      const start = performance.now();
      await new Promise((resolve) => {
        const frame = (now) => {
          const elapsed = now - start;
          if (elapsed >= total) { resolve(); return; }
          let acc = 0, si = 0;
          for (let i = 0; i < durations.length; i++) {
            if (elapsed < acc + durations[i]) { si = i; break; }
            acc += durations[i]; si = i;
          }
          const p = Math.min((elapsed - acc) / durations[si], 1);

          ctx.fillStyle = "#0B192C";
          ctx.fillRect(0, 0, W, H);
          const dir = si % 2 ? -1 : 1;
          drawCover(ctx, imgs[si], W, H, 1 + 0.14 * p, dir * p * 24, -p * 24);

          const g = ctx.createLinearGradient(0, H * 0.35, 0, H);
          g.addColorStop(0, "rgba(11,25,44,0)");
          g.addColorStop(1, "rgba(11,25,44,0.92)");
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, W, H);

          ctx.textAlign = "center";
          ctx.fillStyle = "#fff";
          ctx.font = '800 46px "Plus Jakarta Sans", system-ui, sans-serif';
          ctx.shadowColor = "rgba(0,0,0,0.55)";
          ctx.shadowBlur = 14;
          const lines = wrapText(ctx, scenes[si].on_screen_text || "", W - 120);
          let ty = H - 150 - (lines.length - 1) * 56;
          for (const l of lines) { ctx.fillText(l, W / 2, ty); ty += 56; }
          ctx.shadowBlur = 0;

          const segGap = 10, side = 40;
          const segW = (W - side * 2 - segGap * (durations.length - 1)) / durations.length;
          for (let i = 0; i < durations.length; i++) {
            const x = side + i * (segW + segGap);
            ctx.fillStyle = "rgba(255,255,255,0.3)";
            roundRect(ctx, x, 42, segW, 7, 3.5); ctx.fill();
            const fill = i < si ? 1 : i === si ? p : 0;
            if (fill > 0) {
              ctx.fillStyle = accent;
              roundRect(ctx, x, 42, segW * fill, 7, 3.5); ctx.fill();
            }
          }
          if (badge) {
            const t = badge.toUpperCase();
            ctx.font = '800 26px "Plus Jakarta Sans", system-ui, sans-serif';
            const bpad = 20, bh = 48, bw = ctx.measureText(t).width + bpad * 2;
            const bx = (W - bw) / 2, by = 66;
            ctx.fillStyle = accent;
            roundRect(ctx, bx, by, bw, bh, bh / 2); ctx.fill();
            ctx.fillStyle = "#0B192C"; ctx.textAlign = "center";
            ctx.fillText(t, W / 2, by + bh / 2 + 9);
          }
          requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      });

      rec.stop();
      await stopped;
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "marine-campaign-15s.webm";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Video downloaded (.webm)", { id: "vid-dl" });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't record this one. Boats with photos uploaded to your dealership work best.");
    } finally {
      setRecording(false);
    }
  };

  const photo = photos.length ? photos[idx % photos.length] : "";
  const scene = scenes[idx] || {};

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-48 rounded-[2rem] border-8 border-navy overflow-hidden bg-navy" style={{ aspectRatio: "9/19.5" }} data-testid="video-mockup">
        {photo && (
          <img
            key={idx}
            src={resolveImg(photo)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={playing ? { animation: `kenburns ${durations[idx]}ms ease-out both`, animationDirection: idx % 2 ? "reverse" : "normal" } : {}}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-navy/20" />

        <div className="absolute top-2 left-3 right-3 flex gap-1">
          {scenes.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden">
              <div
                className={`h-full ${i < idx ? "w-full" : ""}`}
                style={{ backgroundColor: accent, ...(i === idx && playing ? { animation: `growbar ${durations[idx]}ms linear both` } : (i >= idx ? { width: 0 } : {})) }}
              />
            </div>
          ))}
        </div>
        {badge && <div className="absolute top-5 left-1/2 -translate-x-1/2 text-[9px] font-extrabold uppercase tracking-wider text-navy px-2 py-0.5 rounded-full shadow" style={{ backgroundColor: accent }} data-testid="video-badge">{badge}</div>}

        <div className="absolute bottom-6 left-3 right-3 text-white text-center">
          <div key={`t${idx}`} className="font-bold text-sm leading-tight drop-shadow-lg fade-up">{scene.on_screen_text}</div>
        </div>

        {recording ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-navy/70 text-white gap-2" data-testid="video-recording">
            <Loader2 className="w-7 h-7 animate-spin text-gold" />
            <span className="text-xs">Recording ~15s…</span>
          </div>
        ) : !playing ? (
          <button onClick={toggle} data-testid="video-play-btn" className="absolute inset-0 flex items-center justify-center group" aria-label="Play preview">
            <span className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center border border-white/50 group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 text-white fill-white ml-0.5" />
            </span>
          </button>
        ) : (
          <button onClick={toggle} data-testid="video-pause-btn" className="absolute inset-0" aria-label="Pause preview" />
        )}
      </div>

      <div className="flex items-center gap-4">
        <button onClick={toggle} disabled={recording} className="inline-flex items-center gap-1.5 text-navy font-semibold text-sm disabled:opacity-40" data-testid="video-toggle-btn">
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {playing ? "Pause" : "Play 0:15"}
        </button>
        <button onClick={downloadVideo} disabled={recording} className="inline-flex items-center gap-1.5 gold-gradient text-navy font-bold text-sm px-4 py-2 rounded-full disabled:opacity-60 gold-sheen" data-testid="video-download-btn">
          {recording ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {recording ? "Recording…" : "Download Video"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground text-center px-2">Downloads a .webm made from your real photos only - pan/zoom &amp; text overlays. No AI-altered imagery. Recording runs in real time (~15s).</p>
    </div>
  );
}
